import React from 'react';
import {
    Grid,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Paper,
    Box,
    FormHelperText,
    FormLabel
} from '@mui/material';
import {
    SmokingRooms as SmokingIcon,
    Pets as PetsIcon,
    WbTwilight as LifeRhythmIcon,
    CleaningServices as CleanlinessIcon,
    Chat as CommunicationIcon,
    HomeWork as StayingHomeIcon,
    People as VisitorsIcon
} from '@mui/icons-material';

function LifestyleForm({ data, onChange }) {
    const handleMatchingPreferenceChange = (field, value) => {
        const updatedData = {
            ...data,
            profile: {
                ...data.profile,
                matchingPreferences: {
                    ...(data.profile.matchingPreferences || {}),
                    [field]: value
                }
            }
        };
        onChange(updatedData);
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    ライフスタイル設定
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    あなたの生活スタイルや希望する居住環境についてお答えください。これらの情報は最適なルームメイトを見つけるために活用されます。
                </Typography>
            </Grid>

            {/* 喫煙 */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <SmokingIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            喫煙
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.smoking || 'non-smoker'}
                            onChange={(e) => handleMatchingPreferenceChange('smoking', e.target.value)}
                        >
                            <FormControlLabel
                                value="non-smoker"
                                control={<Radio />}
                                label="喫煙しない"
                            />
                            <FormControlLabel
                                value="smoker"
                                control={<Radio />}
                                label="喫煙する"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* ペット */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PetsIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            ペット
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.pets || 'no'}
                            onChange={(e) => handleMatchingPreferenceChange('pets', e.target.value)}
                        >
                            <FormControlLabel
                                value="no"
                                control={<Radio />}
                                label="ペットを飼っていない"
                            />
                            <FormControlLabel
                                value="yes"
                                control={<Radio />}
                                label="ペットを飼っている"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* 生活リズム */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LifeRhythmIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            生活リズム
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.lifeRhythm || 'morning'}
                            onChange={(e) => handleMatchingPreferenceChange('lifeRhythm', e.target.value)}
                        >
                            <FormControlLabel
                                value="morning"
                                control={<Radio />}
                                label="朝型（23時までに就寝、7時までに起床が多い）"
                            />
                            <FormControlLabel
                                value="night"
                                control={<Radio />}
                                label="夜型（24時以降に就寝、8時以降に起床が多い）"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* 清潔さ */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CleanlinessIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            清潔さに対する姿勢
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.cleanliness || 'clean'}
                            onChange={(e) => handleMatchingPreferenceChange('cleanliness', e.target.value)}
                        >
                            <FormControlLabel
                                value="clean"
                                control={<Radio />}
                                label="きれい好き（定期的に掃除、物は常に整理整頓）"
                            />
                            <FormControlLabel
                                value="relaxed"
                                control={<Radio />}
                                label="ある程度の散らかりは気にならない"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* コミュニケーション */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CommunicationIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            コミュニケーションスタイル
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.communication || 'active'}
                            onChange={(e) => handleMatchingPreferenceChange('communication', e.target.value)}
                        >
                            <FormControlLabel
                                value="active"
                                control={<Radio />}
                                label="交流型（会話や食事など、ある程度の交流を希望）"
                            />
                            <FormControlLabel
                                value="minimal"
                                control={<Radio />}
                                label="独立型（プライバシーを重視、必要最低限の交流で十分）"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* 在宅時間 */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <StayingHomeIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            在宅状況
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.stayingHome || 'often'}
                            onChange={(e) => handleMatchingPreferenceChange('stayingHome', e.target.value)}
                        >
                            <FormControlLabel
                                value="often"
                                control={<Radio />}
                                label="在宅が多い（リモートワークや在宅時間が長い）"
                            />
                            <FormControlLabel
                                value="rarely"
                                control={<Radio />}
                                label="外出が多い（日中はほとんど家にいない）"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>

            {/* 来客 */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <VisitorsIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            友人や知人を家に招くこと
                        </Typography>
                    </Box>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={data.profile.matchingPreferences?.visitors || 'welcome'}
                            onChange={(e) => handleMatchingPreferenceChange('visitors', e.target.value)}
                        >
                            <FormControlLabel
                                value="welcome"
                                control={<Radio />}
                                label="招くことがある（月に数回程度は友人を招きたい）"
                            />
                            <FormControlLabel
                                value="rarely"
                                control={<Radio />}
                                label="ほとんど招かない（特別な場合を除き、外で会うことが多い）"
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default LifestyleForm; 